import { useEffect, useState } from "react";
import { ClientService } from "../../component/services/client.service";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import moment from "moment";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBuilding,
    faCheckCircle,
    faTimesCircle,
    faUserCheck,
} from "@fortawesome/free-solid-svg-icons";

interface Props {
    dateRange: DateRange;
}

interface VendorStat {
    vendorName: string;
    totalRegistered: number;
    totalReverification: number;
    totalSuccessReverification: number;
    totalFailureReverification: number;
    distinctSuccessStudents: number;
    failedDistinctStudents: number;
}

function distinctSuccessRate(success: number, failure: number) {
    const total = success + failure;
    if (!total) return 0;
    return Math.round((success / total) * 100);
}

function RateBadge({ rate }: { rate: number }) {
    const color = rate >= 80 ? "#10b981" : rate >= 50 ? "#f59e0b" : "#ef4444";
    const bg = rate >= 80 ? "#d1fae5" : rate >= 50 ? "#fef3c7" : "#fee2e2";
    return (
        <span
            style={{
                background: bg,
                color,
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: "20px",
                padding: "2px 10px",
                display: "inline-block",
            }}
        >
            {rate}%
        </span>
    );
}

export default function VendorOverView({ dateRange }: Props) {
    const { colors, colorSchemes } = useTheme();
    const [vendorStats, setVendorStats] = useState<VendorStat[]>([]);
    const [loading, setLoading] = useState(false);

    async function getVendorStats(start: Date, end: Date) {
        setLoading(true);
        const params = makeParams([
            { index: "start_time", value: moment(start).toISOString() },
            { index: "end_time", value: moment(end).toISOString() },
        ]);
        await ClientService.getVendorReverficationStats(params)
            .then((res: any) => setVendorStats(res.data?.data || []))
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getVendorStats(dateRange.startDate, dateRange.endDate);
    }, [dateRange.startDate, dateRange.endDate]);

    const totals = vendorStats.reduce(
        (acc, v) => ({
            attempted: acc.attempted + v.totalRegistered,
            distinctSuccess: acc.distinctSuccess + v.distinctSuccessStudents,
            distinctFailure: acc.distinctFailure + v.failedDistinctStudents,
        }),
        { attempted: 0, distinctSuccess: 0, distinctFailure: 0 }
    );

    const overallRate = distinctSuccessRate(totals.distinctSuccess, totals.distinctFailure);

    const summaryCards = [
        { label: "Students Attempted", value: totals.attempted, icon: faBuilding, color: colors.primary.main, bg: colors.background.tertiary },
        { label: "Distinct Success", value: totals.distinctSuccess, icon: faCheckCircle, color: "#10b981", bg: "#d1fae5" },
        { label: "Distinct Failure", value: totals.distinctFailure, icon: faTimesCircle, color: "#ef4444", bg: "#fee2e2" },
        { label: "Success Rate", value: `${overallRate}%`, icon: faUserCheck, color: overallRate >= 80 ? "#10b981" : overallRate >= 50 ? "#f59e0b" : "#ef4444", bg: overallRate >= 80 ? "#d1fae5" : overallRate >= 50 ? "#fef3c7" : "#fee2e2" },
    ];

    const tdBase: React.CSSProperties = {
        padding: "14px 16px",
        borderBottom: `1px solid ${colors.border.light}`,
        fontSize: "0.88rem",
    };

    return (
        <>
            {/* Summary stat cards */}
            {vendorStats.length > 0 && (
                <Row className="g-3 mb-4">
                    {summaryCards.map((card) => (
                        <Col key={card.label} xs={6} md={3}>
                            <Card
                                style={{
                                    ...colorSchemes.card,
                                    borderRadius: "12px",
                                    border: `1px solid ${colors.border.light}`,
                                }}
                            >
                                <Card.Body className="p-3">
                                    <div
                                        className="mb-2 d-flex align-items-center justify-content-center"
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "8px",
                                            background: card.bg,
                                        }}
                                    >
                                        <FontAwesomeIcon icon={card.icon} style={{ color: card.color, fontSize: "1rem" }} />
                                    </div>
                                    <div style={{ fontWeight: 700, fontSize: "1.25rem", color: colors.text.primary }}>
                                        {typeof card.value === "number" ? card.value.toLocaleString() : card.value}
                                    </div>
                                    <div style={{ fontSize: "0.72rem", color: colors.text.muted, marginTop: 2 }}>
                                        {card.label}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Table */}
            <Card
                style={{
                    ...colorSchemes.card,
                    borderRadius: "16px",
                    border: `1px solid ${colors.border.light}`,
                    overflow: "hidden",
                }}
            >
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="d-flex align-items-center justify-content-center py-5">
                            <Spinner animation="border" style={{ color: colors.primary.main }} />
                        </div>
                    ) : vendorStats.length === 0 ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                            <FontAwesomeIcon
                                icon={faBuilding}
                                style={{ fontSize: "2.5rem", color: colors.border.secondary, marginBottom: "1rem" }}
                            />
                            <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>
                                No data found for the selected range
                            </span>
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ background: colors.background.secondary }}>
                                        {["Vendor", "Students Attempted", "Distinct Success", "Distinct Failure", "Success Rate"].map((h) => (
                                            <th
                                                key={h}
                                                style={{
                                                    padding: "12px 16px",
                                                    fontSize: "0.72rem",
                                                    fontWeight: 600,
                                                    color: colors.text.muted,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    borderBottom: `1px solid ${colors.border.light}`,
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendorStats.map((vendor, i) => (
                                        <tr
                                            key={vendor.vendorName}
                                            style={{
                                                background: i % 2 === 0 ? colors.background.primary : colors.background.secondary,
                                                transition: "background 0.15s",
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = colors.background.tertiary)}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? colors.background.primary : colors.background.secondary)}
                                        >
                                            {/* Vendor name */}
                                            <td style={{ ...tdBase, fontWeight: 600, color: colors.text.primary }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        style={{
                                                            width: 30, height: 30, borderRadius: "8px",
                                                            background: colors.background.tertiary,
                                                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faBuilding} style={{ color: colors.primary.main, fontSize: "0.8rem" }} />
                                                    </div>
                                                    {vendor.vendorName}
                                                </div>
                                            </td>
                                            {/* Students Attempted */}
                                            <td style={{ ...tdBase, color: colors.text.secondary }}>
                                                {vendor.totalRegistered.toLocaleString()}
                                            </td>
                                            {/* Distinct Success */}
                                            <td style={{ ...tdBase, color: "#10b981", fontWeight: 500 }}>
                                                {vendor.distinctSuccessStudents.toLocaleString()}
                                            </td>
                                            {/* Distinct Failure */}
                                            <td style={{ ...tdBase, color: "#ef4444", fontWeight: 500 }}>
                                                {vendor.failedDistinctStudents.toLocaleString()}
                                            </td>
                                            {/* Success Rate (distinct based) */}
                                            <td style={tdBase}>
                                                <RateBadge rate={distinctSuccessRate(vendor.distinctSuccessStudents, vendor.failedDistinctStudents)} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </>
    );
}
